/**
 * Navigation
 * ==========
 * Fixed header that sits directly on the dark canvas — gains a hairline
 * border and blur once scrolled rather than swapping colour. Full-screen
 * stagger-reveal menu on mobile.
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_LINKS, BUSINESS_INFO, EXTERNAL_URLS } from '../config/constants';
import Button from './ui/Button';
import { prefetchRoute } from '../pages/routes';
import { rafThrottle } from '../utils/rafThrottle';

const PRIMARY_LINKS = NAV_LINKS.filter((l) => l.path !== '/order-online' && l.path !== '/book-table');

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkScrolled = () => setIsScrolled(window.scrollY > 20);
    const handleScroll = rafThrottle(checkScrolled);
    checkScrolled();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMenuOpen]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-ink/90 backdrop-blur-md border-b border-line'
            : 'bg-gradient-to-b from-black/75 via-black/50 to-black/20 backdrop-blur-[2px] border-b border-white/10'
        }`}
        role="banner"
      >
        <nav className="container-custom" aria-label="Main navigation">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0" aria-label={`${BUSINESS_INFO.shortName} - Home`}>
              <img
                src="/images/branding/logo.svg"
                alt=""
                aria-hidden="true"
                className="h-16 lg:h-20 w-auto transition-opacity duration-300 ease-out group-hover:opacity-80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
              />
              <span className="hidden sm:flex flex-col leading-none">
                <span className="font-display text-xl lg:text-2xl text-cream [text-shadow:0_1px_3px_rgba(0,0,0,0.5)]">Gup Shup</span>
                <span className="font-sans text-2xs tracking-widest2 uppercase text-cream-dim mt-1 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">Chit Chat Chai</span>
              </span>
            </Link>

            {/* Desktop links */}
            <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
              {PRIMARY_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onMouseEnter={() => prefetchRoute(link.path)}
                  onFocus={() => prefetchRoute(link.path)}
                  className={`relative px-1 xl:px-4 py-2 font-sans text-sm whitespace-nowrap transition-colors [text-shadow:0_1px_2px_rgba(0,0,0,0.5)] ${
                    isActive(link.path) ? 'text-amber-bright' : 'text-cream hover:text-amber-bright'
                  }`}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <motion.span layoutId="nav-active" className="absolute left-1 right-1 xl:left-4 xl:right-4 -bottom-0.5 h-px bg-amber-bright" />
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-5 flex-shrink-0">
              <Button href={EXTERNAL_URLS.reservation} variant="text" className="[text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
                Book a Table
              </Button>
              <Button href={EXTERNAL_URLS.ordering} variant="text" className="[text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
                Order Collection
              </Button>
              <Button to="/order-online" variant="primary">
                Order Online
              </Button>
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              className="lg:hidden p-2 -mr-2 text-cream min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setIsMenuOpen((o) => !o)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <div className="w-6 h-5 relative drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                <span className={`absolute h-px w-6 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 top-2' : 'top-0'}`} />
                <span className={`absolute h-px w-6 bg-current top-2 transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                <span className={`absolute h-px w-6 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 top-2' : 'top-4'}`} />
              </div>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            id="mobile-menu"
            className="lg:hidden fixed inset-0 z-40 bg-ink"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-full overflow-y-auto pt-28 pb-12 px-6 flex flex-col">
              <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
                {PRIMARY_LINKS.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Link
                      to={link.path}
                      className={`block py-3 font-display text-3xl ${isActive(link.path) ? 'text-amber-bright' : 'text-cream'}`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              <motion.div
                className="mt-10 flex flex-col gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
              >
                <Button to="/order-online" variant="primary" className="w-full">Order Online</Button>
                <Button href={EXTERNAL_URLS.ordering} variant="outline" className="w-full">Order Collection</Button>
                <Button href={EXTERNAL_URLS.reservation} variant="outline" className="w-full">Book a Table</Button>
              </motion.div>

              <motion.div
                className="mt-auto pt-10 border-t border-line"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                <p className="text-cream-faint text-xs uppercase tracking-widest2 mb-2">Visit us</p>
                <p className="text-cream-dim font-sans text-sm mb-4">{BUSINESS_INFO.address.full}</p>
                <a href={`tel:${BUSINESS_INFO.phone.replace(/\s/g, '')}`} className="text-amber-bright font-sans font-semibold">
                  {BUSINESS_INFO.phone}
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
